/*
 * File: fast-json-stable-stringify
 * File Created: 2024-08-28 12:36:35
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-09-16 03:36:58
 * Modified By: Val Liu
 * -----
 */

type CompareFunction = (
    a: { key: string; value: any },
    b: { key: string; value: any },
) => number;

interface StringifyOptions {
    cmp?: CompareFunction;
    cycles?: boolean;
}

export function fastJSONStableStringify(
    data: any,
    opts: StringifyOptions | CompareFunction = { cycles: true },
): string | undefined {
    if (data == undefined) return undefined;
    if (!opts) opts = {};
    if (typeof opts === "function") opts = { cmp: opts };
    const cycles = typeof opts.cycles === "boolean" ? opts.cycles : false;

    const cmp = opts.cmp
        ? ((f: CompareFunction) => {
              return (node: any) => {
                  return (a: string, b: string) => {
                      const aobj = { key: a, value: node[a] };
                      const bobj = { key: b, value: node[b] };
                      return f(aobj, bobj);
                  };
              };
          })(opts.cmp)
        : undefined;

    const seen: any[] = [];
    return (function stringifyNode(node: any): string | undefined {
        if (node && typeof node.toJSON === "function") {
            node = node.toJSON();
        }

        if (node === undefined) return;
        if (typeof node === "number") return isFinite(node) ? "" + node : "null";
        if (typeof node !== "object") return JSON.stringify(node);

        let i: number;
        let out: string;
        if (Array.isArray(node)) {
            out = "[";
            for (i = 0; i < node.length; i++) {
                if (i) out += ",";
                out += stringifyNode(node[i]) || "null";
            }
            return out + "]";
        }

        if (node === null) return "null";

        if (seen.indexOf(node) !== -1) {
            if (cycles) return JSON.stringify("__cycle__");
            throw new TypeError("Converting circular structure to JSON");
        }

        const seenIndex = seen.push(node) - 1;
        const keys = Object.keys(node).sort(cmp && cmp(node));
        out = "";
        for (i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = stringifyNode(node[key]);

            if (!value) continue;
            if (out) out += ",";
            out += JSON.stringify(key) + ":" + value;
        }
        seen.splice(seenIndex, 1);
        return "{" + out + "}";
    })(data);
}
